
import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { createUseStyles } from "react-jss";
import { Button, ButtonProps } from "@mui/material";

import styles from "assets/jss/material-dashboard-pro-react/components/buttonStyle.js";

const useStyles = createUseStyles(styles);

type ButtonColor =
  | "primary"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "transparent"
  | "orangePeel";

interface RegularButtonProps
  extends Omit<ButtonProps, "color" | "size" | "classes"> {
  color?: ButtonColor;
  size?: "sm" | "lg";
  round?: boolean;
  simple?: boolean;
  fullWidth?: boolean;
  block?: boolean;
  link?: boolean;
  justIcon?: boolean;
  muiClasses?: ButtonProps["classes"];
}

const RegularButton = React.forwardRef<HTMLButtonElement, RegularButtonProps>(
  (props, ref) => {
    const classes = useStyles();

    const {
      color,
      round,
      children,
      fullWidth,
      disabled,
      simple,
      size,
      block,
      link,
      justIcon,
      className,
      muiClasses,
      ...rest
    } = props;

    const btnClasses = classNames(
      classes.button,
      size && classes[size],
      color && classes[color],
      round && classes.round,
      fullWidth && classes.fullWidth,
      disabled && classes.disabled,
      simple && classes.simple,
      block && classes.block,
      link && classes.link,
      justIcon && classes.justIcon,
      className
    );

    return (
      <Button
        {...rest}
        ref={ref}
        classes={muiClasses}
        className={btnClasses}
      >
        {children}
      </Button>
    );
  }
);

RegularButton.displayName = "RegularButton";

RegularButton.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "info",
    "success",
    "warning",
    "danger",
    "rose",
    "white",
    "twitter",
    "facebook",
    "google",
    "linkedin",
    "pinterest",
    "youtube",
    "tumblr",
    "github",
    "behance",
    "dribbble",
    "reddit",
    "transparent",
    "orangePeel",
  ]),
  size: PropTypes.oneOf(["sm", "lg"]),
  simple: PropTypes.bool,
  round: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  block: PropTypes.bool,
  link: PropTypes.bool,
  justIcon: PropTypes.bool,
  className: PropTypes.string,
  muiClasses: PropTypes.object,
  children: PropTypes.node,
};

export default RegularButton;